import { useLocation, useSearchParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { getToken } from "@/utils/auth";
import { getApiOrigin } from "@/config/env";

interface ProjectContext {
    projectId: string | null;
    projectName: string | null;
    isInProject: boolean;
}

export function useProjectContext(): ProjectContext {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [projectName, setProjectName] = useState<string | null>(null);
    const [lastFetchedProjectId, setLastFetchedProjectId] = useState<string | null>(null);

    // Extract projectId from URL path or query parameters
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const projectIndex = pathSegments.indexOf("projects");
    const projectIdFromPath = projectIndex !== -1 && pathSegments[projectIndex + 1]
        ? pathSegments[projectIndex + 1]
        : null;
    
    // Also check for projectId in query parameters
    const projectIdFromQuery = searchParams.get("projectId");
    
    // Use projectId from path first, then from query
    const projectId = projectIdFromPath || projectIdFromQuery;

    const isInProject = projectId !== null;

    // Fetch project name when projectId is available
    useEffect(() => {
        const fetchProjectName = async () => {
            // Only fetch if projectId exists and is different from the last fetched one
            if (projectId && projectId !== lastFetchedProjectId) {
                try {
                    const res = await fetch (
                        `${getApiOrigin()}/projects/${projectId}`,
                        {
                            headers: { Authorization: `Bearer ${getToken()}` }
                        }
                    );

                    if (res.ok) {
                        const json = await res.json();
                        const project = json.data;
                        setProjectName(project?.name || "Projects");
                        setLastFetchedProjectId(projectId);
                    } else {
                        setProjectName("Projects");
                        setLastFetchedProjectId(projectId);
                    }
                } catch (error) {
                    console.error("Failed to fetch project:", error);
                    setProjectName("Projects");
                    setLastFetchedProjectId(projectId);
                }
            } else if (!projectId) {
                setProjectName(null);
                setLastFetchedProjectId(null);
            }
        };

        fetchProjectName();
    }, [projectId, lastFetchedProjectId]); // Only depend on stable values, not searchParams object

    // ✅ Memoize the return object to prevent causing re-renders in components that use this hook
    return useMemo(() => ({
        projectId,
        projectName,
        isInProject
    }), [projectId, projectName, isInProject]);
}